// extCustomColumns.saga.js
import { call, put, all, takeLatest, select } from "redux-saga/effects";
import * as actionTypes from "./actionTypes";
import * as extractionActionTypes from "../extractions/actionTypes";
import * as extCustomColumnActionTypes from "../extCustomColumns/actionTypes";
import * as extCustomColumnQueryActionTypes from "../extCustomColumnQueries/actionTypes";
import API from "../../commonUtils/API.js";
import { toast } from "react-toastify";
import { handleApiError } from "../../commonUtils/Functions";
import constants from "../../commonUtils/constants";
import { setExtVersion } from "../extVersions/actions";

// Sequence guard to ignore stale GET_EXT_CUSTOM_COLUMN responses after form resets.
let latestCustomColumnRequestSeq = 0;

function* getExtCustomColumnsSaga({ params }) {
    // const { currentPage, perPage } = params;

    try {
        yield put({ type: actionTypes.SET_LOADING, payload: true });

        const filters = yield select((state) => state.ExtCustomColumns.filters);
        const extractionData = yield select((state) => state.Extractions.extractionData);

        let queryString = "";
        // let queryString = `currentPage=${currentPage}&perPage=${perPage}`;
        if(filters.keyword) queryString = queryString.concat(`&keyword=${encodeURIComponent(filters.keyword)}`);

        const { data } = yield API.get(`/ext/${extractionData.id}/custom-columns?${queryString}`);
        if (data.success) {
            yield put({ type: actionTypes.GET_EXT_CUSTOM_COLUMNS_SUCCESS, payload: data.data });
        } else {
            toast.error("Oops! Something went unexpectedly wrong.");
            yield put({ type: actionTypes.GET_EXT_CUSTOM_COLUMNS_FAILURE, payload: data });
        }

    } catch (error) {
        handleApiError(error, "Oops! Something went unexpectedly wrong.");
        const data = error?.response?.data;
        yield put({ type: actionTypes.GET_EXT_CUSTOM_COLUMNS_FAILURE, payload: data || { message: "Internal server error" } });
    }
};

function* getExtCustomColumnSaga({ id }) {
    try {
        const requestSeq = ++latestCustomColumnRequestSeq;
        yield put({ type: actionTypes.SET_LOADING, payload: true });
        const extractionData = yield select((state) => state.Extractions.extractionData);
        if (!extractionData?.id) {
            toast.error("Extraction ID not found. Please refresh the page.");
            yield put({ 
                type: actionTypes.GET_EXT_CUSTOM_COLUMN_FAILURE, 
                payload: { message: "Extraction ID not found" }
            });
            return;
        }

        const { data } = yield call(API.get, `/ext/${extractionData.id}/custom-columns/${id}`);

        // Ignore stale responses if a newer request or reset happened while awaiting API.
        if (requestSeq !== latestCustomColumnRequestSeq) {
            return;
        }
        
        if (data.success) {
            // ✅ First load the column data WITHOUT opening the drawer
            yield put({ type: actionTypes.GET_EXT_CUSTOM_COLUMN_SUCCESS, payload: data.data });
            
            // ✅ Then load queries
            yield put({ 
                type: extCustomColumnQueryActionTypes.GET_EXT_CUSTOM_COLUMN_QUERIES_SUCCESS, 
                payload: { data: data.data.queries || [] } 
            });
            
            // ✅ Open drawer LAST — after all state is settled
            // This prevents Radix from mounting the Sheet while Redux is mid-update
            yield put({ type: actionTypes.SET_OPEN_DRAWER, payload: true });
        } else {
            toast.error("Could not load custom column.");
            yield put({ type: actionTypes.GET_EXT_CUSTOM_COLUMN_FAILURE, payload: data });
        }

    } catch (error) {
        handleApiError(error, "Could not load custom column.");
        const data = error?.response?.data;
        yield put({ type: actionTypes.GET_EXT_CUSTOM_COLUMN_FAILURE, payload: data || { message: "Internal server error" } });
    }
}

function* createExtCustomColumnSaga({ payload }) {
    try {
        yield put({ type: actionTypes.SET_LOADING, payload: true });
        
        const { extCustomColumn } = payload;
        const extractionData = yield select((state) => state.Extractions.extractionData);
        
        if (!extractionData?.id) {
            toast.error("Extraction ID not found. Please refresh the page.");
            yield put({ 
                type: actionTypes.CREATE_EXT_CUSTOM_COLUMN_FAILURE, 
                payload: { message: "Extraction ID not found" } 
            });
            return;
        }
        
        const { data } = yield API.post(`/ext/${extractionData.id}/custom-columns`, extCustomColumn);
        if (data.success) {
            // toast.success("Custom column created successfully!");
            yield put({ type: actionTypes.CREATE_EXT_CUSTOM_COLUMN_SUCCESS, payload: data.data });
            const extVersion = yield select((state) => state.ExtVersions.extVersion);
            yield put(setExtVersion({
                ...extVersion,
                custom_columns: [ ...extVersion.custom_columns, data.data.id ],
            }));
            yield put({ type: actionTypes.GET_EXT_CUSTOM_COLUMNS });
            yield put({ type: extractionActionTypes.LOAD_CUSTOM_COLUMN_OPTIONS });
        } else {
            toast.error(data?.message || "Could not save custom column.");
            yield put({ type: actionTypes.CREATE_EXT_CUSTOM_COLUMN_FAILURE, payload: data?.data || data });
        }
    } catch (error) {
        handleApiError(error, "Could not save custom column.");
        const data = error?.response?.data;
        yield put({ type: actionTypes.CREATE_EXT_CUSTOM_COLUMN_FAILURE, payload: data || { message: "Internal server error" } });
    }
};

export function* updateAndAttachExtCustomColumnSaga({ payload }) {
    try {
        yield put({ type: actionTypes.SET_LOADING, payload: true });
        const { id, formData } = payload;
        const extractionData = yield select((state) => state.Extractions.extractionData);

        if (!extractionData?.id) {
            toast.error("Extraction ID not found. Please refresh the page.");
            yield put({ type: actionTypes.UPDATE_EXT_CUSTOM_COLUMN_FAILURE, payload: { message: "Extraction ID not found" } });
            return;
        }

        const { data } = yield API.put(
            `/ext/${extractionData.id}/custom-columns/${id}/update-and-attach`,
            formData
        );

        if (data.success) {
            toast.success("Custom column updated successfully!");
            yield put({ type: actionTypes.UPDATE_EXT_CUSTOM_COLUMN_SUCCESS, payload: data.data });

            const extVersion = yield select((state) => state.ExtVersions.extVersion);
            const updatedCustomColumns = [...new Set([...extVersion.custom_columns, id])];

            // Keep selected columns in sync with both used_fields and replace_with.
            const usedFields = data.data?.used_fields || [];
            const replaceWithFields = data.data?.replace_with || [];
            const newSelectedCols = [
                ...new Set([...(extVersion.selected_columns || []), ...usedFields, ...replaceWithFields]),
            ];

            yield put(setExtVersion({
                ...extVersion,
                custom_columns: updatedCustomColumns,
                selected_columns: newSelectedCols,
            }));

            // Refresh sources consumed by ExtCustomColumnContext filtering logic.
            yield put({ type: actionTypes.GET_EXT_CUSTOM_COLUMNS });
            yield put({ type: extractionActionTypes.LOAD_CUSTOM_COLUMN_OPTIONS });

            // Persist both temp fields to backend
            yield API.patch(`/ext/${extractionData.id}/temp-selections`, {
                selected_columns: newSelectedCols,
                custom_columns: updatedCustomColumns,
            });

        } else {
            toast.error(data?.message || "Could not save updated custom column data.");
            yield put({ type: actionTypes.UPDATE_EXT_CUSTOM_COLUMN_FAILURE, payload: data });
        }
    } catch (error) {
        const errorData = error?.response?.data;
        if (errorData?.data?.errors) {
            toast.error("Please fix validation errors");
        } else {
            handleApiError(error, "Could not save updated custom column data.");
        }
        yield put({
            type: actionTypes.UPDATE_EXT_CUSTOM_COLUMN_FAILURE,
            payload: {
                message: errorData?.message || "Internal server error",
                errors: errorData?.data?.errors || null
            }
        });
    }
}

function* updateExtCustomColumnsOrderSaga({ payload }) {
    try {
        const { orderData } = payload;
        const extractionData = yield select((state) => state.Extractions.extractionData);

        if (!extractionData?.id) {
            toast.error("Extraction ID not found. Please refresh the page.");
            yield put({ 
                type: actionTypes.UPDATE_EXT_CUSTOM_COLUMNS_ORDER_FAILURE, 
                payload: { message: "Extraction ID not found" } 
            });
            return;
        }

        const { data } = yield API.put(
            `/ext/${extractionData.id}/custom-columns/update-order`, 
            { orderData }
        );

        if (data.success) {
            // Success toast removed for better UX
            // toast.success("Order updated successfully!");
            yield put({ 
                type: actionTypes.UPDATE_EXT_CUSTOM_COLUMNS_ORDER_SUCCESS, 
                payload: data.data 
            });
            
            // Optional: Refresh the list only on page change or filter change
            // Not needed here since the context already updates the local state
        } else {
            toast.error(data?.message || "Could not update order.");
            yield put({ 
                type: actionTypes.UPDATE_EXT_CUSTOM_COLUMNS_ORDER_FAILURE, 
                payload: data 
            });
        }

    } catch (error) {
        handleApiError(error, "Could not update order.");
        const data = error?.response?.data;
        
        // On error, we might want to refresh the list to revert to correct order
        toast.error("Failed to update order. Refreshing...");
        // const { pagination } = yield select((state) => state.ExtCustomColumns.ExtCustomColumnsList);
        // const params = { 
        //     currentPage: pagination?.currentPage || 1, 
        //     perPage: pagination?.perPage || 10 
        // };
        // yield put({ type: actionTypes.GET_EXT_CUSTOM_COLUMNS, params });
        yield put({ type: actionTypes.GET_EXT_CUSTOM_COLUMNS });
        
        yield put({ 
            type: actionTypes.UPDATE_EXT_CUSTOM_COLUMNS_ORDER_FAILURE, 
            payload: data || { message: "Internal server error" } 
        });
    }
};

function* deleteExtCustomColumnSaga({ Id }) {
    try {
        yield put({ type: actionTypes.SET_LOADING, payload: true });

        const extractionData = yield select((state) => state.Extractions.extractionData);
        const { data } = yield API.delete(`/ext/${extractionData.id}/custom-columns/${Id}`);

        if (data.success) {
            // const { pagination } = yield select((state) => state.ExtCustomColumns.ExtCustomColumnsList);
            // const params = { currentPage: pagination.currentPage, perPage: pagination.perPage };
            yield put({ type: actionTypes.DELETE_EXT_CUSTOM_COLUMN_SUCCESS, payload: data });
            // yield put({ type: actionTypes.GET_EXT_CUSTOM_COLUMNS, params });
            yield put({ type: actionTypes.GET_EXT_CUSTOM_COLUMNS });
        } else {
            toast.error(data?.message ||"Could not delete custom column.");
            yield put({ type: actionTypes.DELETE_EXT_CUSTOM_COLUMN_FAILURE, payload: data });
        }

    } catch (error) {
        handleApiError(error, "Could not delete custom column.");
        const data = error?.response?.data;
        toast.error(data?.message ||"Could not delete custom column.");
        yield put({ type: actionTypes.CREATE_EXT_CUSTOM_COLUMN_FAILURE, payload: data || { message: "Internal server error" } });
    }
};

export function* attachExtCustomColumnSaga({ Id }) {
    try {
        yield put({ type: actionTypes.SET_LOADING, payload: true });
        const extractionData = yield select((state) => state.Extractions.extractionData);

        if (!extractionData?.id) {
            toast.error("Extraction ID not found. Please refresh the page.");
            yield put({ 
                type: actionTypes.ATTACH_EXT_CUSTOM_COLUMN_FAILURE, 
                payload: { message: "Extraction ID not found" } 
            });
            return;
        }
        
        const { data } = yield API.post(`/ext/${extractionData.id}/custom-columns/${Id}/attach`);
        if (data.success) {
            toast.success("Custom column attached successfully!");

            // pull down the newly attached column details so we can
            // automatically add its used_fields to the selection list
            let usedFields = [];
            try {
                const { data: colResp } = yield API.get(
                    `/ext/${extractionData.id}/custom-columns/${Id}`
                );
                if (colResp?.success && colResp?.data) {
                    usedFields = colResp.data.used_fields || [];
                }
            } catch (fetchErr) {
                // ignore, we'll just not auto-select fields
                console.warn("Could not fetch custom column for used_fields", fetchErr);
            }
            
            yield put({ 
                type: actionTypes.ATTACH_EXT_CUSTOM_COLUMN_SUCCESS, 
                payload: { Id } 
            });
            
            // Refresh the list so UI shows new column
            yield put({ type: actionTypes.GET_EXT_CUSTOM_COLUMNS });

            // update extVersion state - add column id and any used fields
            const extVersion = yield select((state) => state.ExtVersions.extVersion);
            const newCustomColumns = [...new Set([...(extVersion.custom_columns || []), Id])];
            const newSelectedColumns = [...new Set([...(extVersion.selected_columns || []), ...usedFields])];
            yield put(setExtVersion({
                ...extVersion,
                custom_columns: newCustomColumns,
                selected_columns: newSelectedColumns,
            }));

            // persist the temporary selections too (matches updateAndAttach behavior)
            yield API.patch(`/ext/${extractionData.id}/temp-selections`, {
                selected_columns: newSelectedColumns,
                custom_columns: newCustomColumns,
            });

        } else {
            toast.error(data?.message || "Could not attach custom column.");
            yield put({ 
                type: actionTypes.ATTACH_EXT_CUSTOM_COLUMN_FAILURE, 
                payload: data 
            });
        }

    } catch (error) {
        const errorData = error?.response?.data;
        
        // Check if we have backend validation errors
        if (errorData?.data?.errors) {
            toast.error("Please fix validation errors");
        } else {
            // Generic error handling for non-validation errors
            handleApiError(error, "Could not attach custom column.");
        }
        
        yield put({ 
            type: actionTypes.ATTACH_EXT_CUSTOM_COLUMN_FAILURE, 
            payload: { 
                message: errorData?.message || "Internal server error",
                errors: errorData?.data?.errors || null
            } 
        });
    } finally {
        yield put({ type: actionTypes.SET_LOADING, payload: false });
    }
};

function* detachExtCustomColumnSaga({ Id }) {
    try {
        yield put({ type: actionTypes.SET_LOADING, payload: true });
        const extractionData = yield select((state) => state.Extractions.extractionData);

        if (!extractionData?.id) {
            toast.error("Extraction ID not found. Please refresh the page.");
            yield put({ 
                type: actionTypes.DETACH_EXT_CUSTOM_COLUMN_FAILURE, 
                payload: { message: "Extraction ID not found" } 
            });
            return;
        }
        
        const { data } = yield API.post(`/ext/${extractionData.id}/custom-columns/${Id}/detach`);
        if (data.success) {
            toast.success("Custom column detached successfully!");
            
            // Clear any previous errors on success
            // yield put(setQBErrors({}));
            
            yield put({ 
                type: actionTypes.DETACH_EXT_CUSTOM_COLUMN_SUCCESS, 
                payload: { Id } 
            });
            
            // Refresh the list
            yield put({ type: actionTypes.GET_EXT_CUSTOM_COLUMNS });
        } else {
            toast.error(data?.message || "Could not detach custom column.");
            yield put({ 
                type: actionTypes.DETACH_EXT_CUSTOM_COLUMN_FAILURE, 
                payload: data 
            });
        }

    } catch (error) {
        const errorData = error?.response?.data;
        
        // Check if we have backend validation errors
        if (errorData?.data?.errors) {
            toast.error("Please fix validation errors");
        } else {
            // Generic error handling for non-validation errors
            handleApiError(error, "Could not detach custom column.");
        }
        
        yield put({ 
            type: actionTypes.DETACH_EXT_CUSTOM_COLUMN_FAILURE, 
            payload: { 
                message: errorData?.message || "Internal server error",
                errors: errorData?.data?.errors || null
            } 
        });
    } finally {
        yield put({ type: actionTypes.SET_LOADING, payload: false });
    }
};

function* resetCustomColumnFormSaga() {
    // Invalidate any in-flight GET_EXT_CUSTOM_COLUMN request so stale data is ignored.
    latestCustomColumnRequestSeq += 1;

    yield put({ type: actionTypes.SET_EXT_CUSTOM_COLUMN, payload: constants.extraction.initialState.extCustomColumn });
    yield put({ type: actionTypes.SET_ERRORS, payload: {} });
    yield put({ type: extCustomColumnQueryActionTypes.SET_EXT_CUSTOM_COLUMN_QUERY, payload: null });
    yield put({ type: extCustomColumnQueryActionTypes.SET_OPEN_MODAL, payload: false });
    yield put({ type: extCustomColumnQueryActionTypes.GET_EXT_CUSTOM_COLUMN_QUERIES_SUCCESS, payload: constants.extraction.listInitialState });
    yield put({ type: extCustomColumnActionTypes.SET_OPEN_DRAWER, payload: true });
    yield put({ type: actionTypes.SET_LOADING, payload: false }); // Ensure loading is always reset
}

function* ExtCustomColumnsSaga() {
  yield all([
    takeLatest(actionTypes.GET_EXT_CUSTOM_COLUMNS, getExtCustomColumnsSaga),
    takeLatest(actionTypes.GET_EXT_CUSTOM_COLUMN, getExtCustomColumnSaga),
    takeLatest(actionTypes.CREATE_EXT_CUSTOM_COLUMN, createExtCustomColumnSaga),
    takeLatest(actionTypes.UPDATE_EXT_CUSTOM_COLUMN, updateAndAttachExtCustomColumnSaga),
    takeLatest(actionTypes.UPDATE_EXT_CUSTOM_COLUMNS_ORDER, updateExtCustomColumnsOrderSaga),
    takeLatest(actionTypes.DELETE_EXT_CUSTOM_COLUMN, deleteExtCustomColumnSaga),
    takeLatest(actionTypes.ATTACH_EXT_CUSTOM_COLUMN, attachExtCustomColumnSaga),
    takeLatest(actionTypes.DETACH_EXT_CUSTOM_COLUMN, detachExtCustomColumnSaga),
    takeLatest(actionTypes.RESET_CUSTOM_COLUMN_FORM, resetCustomColumnFormSaga)
  ]);
}

export default ExtCustomColumnsSaga;